import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { Odds, OddsComparerFactory, OddsOperations, emptyOdds } from '@frontend/sports/odds/feature';

import { BetslipType } from '../../../../../core/betslip-type';
import { CriteriaType, IRewardToken, TokenOddsData } from '../../../reward-tokens.model';
import { TokenValidationInput, TokensCriteriaValidatorBase } from '../criteria-validator-base.service';

@Injectable()
export class OverallOddsValidator extends TokensCriteriaValidatorBase<IRewardToken, CriteriaType> {
    override type = CriteriaType.TotalOdds;

    constructor(
        private oddsComparerFactory: OddsComparerFactory,
        private betslipConfig: BetslipConfig,
    ) {
        super();
    }

    isValid(token: IRewardToken, state: TokenValidationInput): boolean {
        if (!token.minimumOdds) {
            return true;
        }

        const minOdds = this.mapMinOdds(token.minimumOdds);
        const picksPrices = state.selectedPicks
            .map((p) => (p.acceptedPrice.price?.nativeOdds ? p.acceptedPrice.price.nativeOdds : p.currentPrice?.nativeOdds))
            .filter(isDefined);
        const totalOdds = state.selectedType === BetslipType.Teaser ? state.teaserTotalOdds! : this.multiplyListOfNativePrices(picksPrices);
        const totalComparer = this.oddsComparerFactory.createComparer(totalOdds);

        return !OddsOperations.isOddsEmpty(totalOdds) && totalComparer.greaterOrEqualThan(minOdds);
    }

    private multiplyListOfNativePrices(prices: Odds[]): Odds {
        if (prices.length === 0) {
            return emptyOdds;
        }

        const calculatedOdds = prices.map((p) => OddsOperations.toCalculatedOdds(p));
        const totalOdds = OddsOperations.getTotalOdds(calculatedOdds, this.betslipConfig.roundPriceValues);

        return OddsOperations.toOdds(totalOdds);
    }

    private mapMinOdds(minimumOdds: TokenOddsData): Odds {
        return OddsOperations.createOdds({
            odds: minimumOdds?.european,
            numerator: minimumOdds?.britishNumerator,
            denominator: minimumOdds?.britishDenominator,
            americanOdds: minimumOdds?.american,
        });
    }
}
